/** @jsxImportSource @emotion/react */
import { CloseOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  getAllGenericSportProperties,
  getSports,
} from '@sportninja/common/selectors/types'
import { Alert, Modal, Tabs } from 'antd'
import React, { useCallback, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import Button from '../ts-components/Button'
import Text from '../ts-components/Text'
import { paragraphMedium, weightRegular } from '../css'
import useWindowSize from 'src/hooks/useWindowSize'
import { useModalScroll } from 'src/hooks/useModalScroll'
import {
  TeamProvider,
  useTeamContext,
  TeamData,
  Sport,
  GenericSport,
} from './TeamContext'
import GeneralSettings from './forms/GeneralSettings'
import ContactInfo from './forms/ContactInfo'

const labelById: Record<string, string> = {
  0: 'General Info',
  1: 'Contact Info',
}

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: hexToRgba(colors.NEUTRAL_900, 0.8),
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

const classNames = {
  body: 'my-modal-body',
  mask: 'my-modal-mask',
  header: 'my-modal-header',
  footer: 'my-modal-footer',
  content: 'my-modal-content',
}

/**
 * Inner component that displays the team modal content with tabbed navigation.
 * Renders the modal UI with General Settings and Contact Info tabs.
 *
 * @param isMobile - Whether the modal is displayed on a mobile device (affects layout)
 * @param isFormOpen - Controls the modal's open/visible state
 * @param onCancel - Callback invoked when the user closes or cancels the modal
 */
const TeamModalContent: React.FC<{
  isMobile: boolean
  isFormOpen: boolean
  onCancel: () => void
}> = ({ isMobile, isFormOpen, onCancel }) => {
  const { state, isEdit, onSubmit, activeTab, setActiveTab } = useTeamContext()

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [onCancel])

  const getFormById = (id: string) => {
    switch (id) {
      case '0':
        return <GeneralSettings isMobile={isMobile} />
      case '1':
        return <ContactInfo isMobile={isMobile} />
      default:
        return null
    }
  }

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        {isEdit ? 'Edit Team' : 'Create New Team'}
      </Text>
    )
  }, [isEdit])

  const getFooter = useCallback(() => {
    return (
      <>
        {state.error && (
          <Alert
            message={state.error}
            type='error'
            showIcon
            css={css`
              margin-bottom: 16px;
              text-align: left;
            `}
          />
        )}
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            width: 50%;
            margin-left: auto;
            ${isMobile &&
            css`
              width: 100%;
              flex-direction: column-reverse;
            `}
          `}
        >
          <Button
            text='Cancel'
            onClick={onCancel}
            variant='secondary'
            disabled={state.isLoading}
          />
          <Button
            text={isEdit ? 'Save' : 'Create'}
            onClick={onSubmit}
            variant='primary'
            isLoading={state.isLoading}
            disabled={state.isLoading}
          />
        </div>
      </>
    )
  }, [state.error, state.isLoading, isMobile, onCancel, isEdit, onSubmit])

  const tabItems = [
    {
      label: labelById[0],
      key: '0',
      children: getFormById('0'),
    },
    {
      label: labelById[1],
      key: '1',
      children: getFormById('1'),
    },
  ]

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100%' : '90vh'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '1080px'};
        top: ${isMobile ? '0' : '50%'};

        .ant-modal-content {
          height: ${isMobile ? '100%' : '90vh'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .ant-tabs-tabpane {
          background: ${colors.SECONDARY_300} !important;
        }
        .ant-tabs-nav {
          min-height: ${isMobile ? 'auto' : '600px'} !important;
        }
        .ant-tabs-tab {
          height: 40px !important;
        }
        .ant-tabs-tab-active {
          background-color: ${colors.SECONDARY_300};
        }
        .ant-tabs-tab-btn {
          ${paragraphMedium}
          ${weightRegular}
        }
        .ant-tabs-tab-btn[aria-selected='true'] {
          color: ${colors.PRIMARY}!important;
        }
        .ant-tabs-tab-btn[aria-selected='false'] {
          color: ${colors.NEUTRAL_30}!important;
        }
        .ant-tabs-tab.ant-tabs-tab-disabled {
          color: ${colors.NEUTRAL_30}!important;
          cursor: not-allowed !important;
          opacity: 0.5 !important;
        }
      `}
      width={isMobile ? '100%' : 1080}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isFormOpen}
      onCancel={onCancel}
      centered
      classNames={classNames}
      styles={modalStyles}
      maskClosable={false}
    >
      <Tabs
        activeKey={activeTab}
        defaultActiveKey='0'
        onChange={(key) => setActiveTab(key)}
        tabPosition={isMobile ? 'top' : 'left'}
        css={css`
          display: flex;
          flex: 1;
          height: 100%;
          background: ${colors.SECONDARY_400} !important;
          ${isMobile &&
          css`
            background: inherit !important;
          `}
          .ant-tabs-top > .ant-tabs-nav {
            margin: 0 !important;
          }
          .ant-tabs-content-holder {
            flex: 1;
            overflow-y: auto;
          }
          .ant-tabs-content {
            height: 100%;
          }
          .ant-tabs-tabpane {
            background: ${colors.SECONDARY_300} !important;
            min-height: 100%;
          }
          .ant-tabs-nav {
            min-height: ${isMobile ? 'auto' : '600px'} !important;
          }
          .ant-tabs-tab {
            ${isMobile ? 'height: 40px !important;' : ''}
            ${isMobile ? 'padding: 0 16px !important;' : ''}
          }
          .ant-tabs-tab-active {
            background-color: ${colors.SECONDARY_300};
          }
          .ant-tabs-tab-btn {
            ${paragraphMedium}
            ${weightRegular}
          }
          .ant-tabs-tab-btn[aria-selected='true'] {
            color: ${colors.PRIMARY}!important;
          }
          .ant-tabs-tab-btn[aria-selected='false'] {
            color: ${colors.NEUTRAL_30}!important;
          }
          .ant-tabs-tab.ant-tabs-tab-disabled {
            color: ${colors.NEUTRAL_30}!important;
            cursor: not-allowed !important;
            opacity: 0.5 !important;
          }
        `}
        items={tabItems}
      />
    </Modal>
  )
}

// Props for the main TeamModal component
interface TeamModalOwnProps {
  isOpen: boolean
  onClose: () => void
  initialState?: TeamData
  organizationId: string
  onSuccess: () => void
  onDelete?: () => void
  permission?: { delete?: boolean; update?: boolean }
  isParentPublic?: boolean
}

interface TeamModalStateProps {
  sports: Sport[]
  genericSports: GenericSport[]
}

type TeamModalProps = TeamModalOwnProps & TeamModalStateProps

const TeamModal: React.FC<TeamModalProps> = ({
  isOpen,
  onClose,
  initialState,
  organizationId,
  onSuccess,
  onDelete,
  sports,
  genericSports,
  permission = {},
  isParentPublic = true,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  const { openModal, closeModal } = useModalScroll()
  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  if (!isOpen) {
    return null
  }

  return (
    <TeamProvider
      isOpen={isOpen}
      onClose={onClose}
      initialState={initialState}
      organizationId={organizationId}
      onSuccess={onSuccess}
      onDelete={onDelete}
      sports={sports}
      genericSports={genericSports}
      permission={permission}
      isParentPublic={isParentPublic}
    >
      <TeamModalContent
        isMobile={isMobile}
        isFormOpen={isOpen}
        onCancel={onClose}
      />
    </TeamProvider>
  )
}

// Redux connect following ScheduleModal pattern
const mapStateToProps = (state: unknown): TeamModalStateProps => {
  return {
    genericSports: getAllGenericSportProperties(state),
    sports: Object.values(getSports(state)),
  }
}

export default connect(mapStateToProps)(TeamModal)
